import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/client/components/page-header";
import { apiQuery, useApiSuspenseQuery } from "@/client/lib/api";
import { ensurePage, pageHead } from "@/client/lib/page";
import { returnToAdminAction, switchToUserAction } from "@/client/modules/auth/actions";
import { CLINIC_VOCAB } from "@/shared/clinic-config";

/** Trocar usuário: o dono vê o app como outra pessoa, sem sair da sessão. */
export const Route = createFileRoute("/app/dev/switch")({
  loader: ({ context }) => ensurePage(context.queryClient, apiQuery("auth", "switchPage", {})),
  head: () => pageHead("Trocar usuário"),
  component: DevSwitchPage,
});

function roleLabel(role: "admin" | "reception"): string {
  return role === "admin" ? CLINIC_VOCAB.professionalLabel : "Recepção";
}

function DevSwitchPage() {
  const { data } = useApiSuspenseQuery("auth", "switchPage", {});
  const { data: me } = useApiSuspenseQuery("auth", "me", {});
  const isImpersonating = Boolean(me.impersonating);
  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      <PageHeader
        title="Trocar usuário"
        description="Visualiza o app como se fosse outro usuário. Só o titular acessa."
      />

      {isImpersonating && me.user && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-semibold text-amber-900">Modo de visualização ativo</div>
              <div className="mt-1 text-[13px] text-amber-800">
                Você está vendo como <span className="font-semibold">{me.user.displayName}</span> (
                {roleLabel(me.user.role)}). Quando terminar, volte para{" "}
                <span className="font-semibold">{me.impersonating?.adminName}</span>.
              </div>
            </div>
            <button
              type="button"
              onClick={() => returnToAdminAction()}
              className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-700"
            >
              Voltar pro admin
            </button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-2">
        <ul className="divide-y divide-slate-100">
          {data.users.map((u) => {
            const isCurrent = u.id === data.currentId;
            return (
              <li key={u.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[15px] font-semibold text-slate-900">{u.displayName}</span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                        u.role === "admin" ? "bg-brand-50 text-brand-800" : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {roleLabel(u.role)}
                    </span>
                    {isCurrent && (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                        Atual
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-[12.5px] text-slate-500">
                    {u.email}
                    {u.doctor ? ` · ${u.doctor.fullName}${u.doctor.crm ? ` (${u.doctor.crm})` : ""}` : ""}
                  </div>
                </div>
                {!isCurrent && (
                  <button
                    type="button"
                    onClick={() => switchToUserAction(u.id)}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:border-brand-500 hover:bg-brand-50 hover:text-brand-800"
                  >
                    Entrar como
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <p className="text-xs text-slate-400">
        Atenção: no modo de visualização, as ações que você fizer (criar prontuário, mandar mensagem etc.) ficam
        registradas em nome do usuário visualizado. Use só para conferir telas.
      </p>
    </div>
  );
}
